/**
 * One user, as a CARD rather than a table row.
 *
 * ⭐⭐ MOBILE-FIRST MEANS THIS IS NOT A TABLE. Eight columns of user administration on a 390px screen
 *   is either a horizontal scroll nobody discovers or a font size nobody can read. A card stacks, and
 *   at ≥768px a grid puts the same fields in columns without a second component to keep in step.
 *   ⛔ The alternative — a `<table>` with `overflow-x-auto` — hides the ACTIONS off-screen on a phone,
 *     which is the half of this screen an admin actually came for.
 */
import type { ReactNode } from 'react';
import type { AdminUser, Id } from '../../api/types';
import { AUTH_METHOD_LABEL, accessState, availableActions, principalLabel } from './user-rows';

const CHIP =
  'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium leading-4';

const ACTION =
  'h-9 rounded-md border border-[color:var(--color-line)] px-3 text-xs font-medium hover:bg-[color:var(--color-surface)] disabled:pointer-events-none disabled:opacity-40';

export interface UserCardProps {
  user: AdminUser;
  currentUserId: Id;
  busy: boolean;
  onSetActive: (user: AdminUser, active: boolean) => void;
  onSetAdmin: (user: AdminUser, grant: boolean) => void;
  onForceReenrol: (user: AdminUser) => void;
  onRevokeSessions: (user: AdminUser) => void;
  onIssueReset: (user: AdminUser) => void;
  /**
   * PH-56 — the membership editor, injected rather than fetched here.
   *
   * ⭐ A RENDER PROP, and optional. The card is rendered in tests and stories that have no
   * membership data; making it required would force every one of them to build a fake. Optional
   * means the card degrades to exactly what it was before this feature existed.
   */
  renderProjects?: (user: AdminUser) => ReactNode;
}

export function UserCard({
  user,
  currentUserId,
  busy,
  onSetActive,
  onSetAdmin,
  onForceReenrol,
  onRevokeSessions,
  onIssueReset,
  renderProjects,
}: UserCardProps) {
  const access = accessState(user);
  const actions = availableActions(user, currentUserId);

  const accessChip =
    access.kind === 'ok'
      ? 'border-emerald-300 text-emerald-800 dark:border-emerald-800 dark:text-emerald-200'
      : access.kind === 'attention'
        ? 'border-amber-300 text-amber-900 dark:border-amber-700 dark:text-amber-200'
        : 'border-red-300 text-red-800 dark:border-red-800 dark:text-red-200';

  return (
    <li className="rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">
            {user.display_name}
            {actions.isSelf ? (
              <span className="ml-2 text-xs font-normal text-[color:var(--color-ink-subtle)]">
                (you)
              </span>
            ) : null}
          </p>
          <p className="truncate text-xs text-[color:var(--color-ink-muted)]">{user.email}</p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {/* ⭐ Person vs Agent FIRST — it is the coarsest distinction and the one that changes how
              every other field should be read. */}
          <span className={`${CHIP} border-[color:var(--color-line)] text-[color:var(--color-ink-muted)]`}>
            {principalLabel(user)}
          </span>
          <span className={`${CHIP} border-[color:var(--color-line)] text-[color:var(--color-ink-muted)]`}>
            {AUTH_METHOD_LABEL[user.auth_method]}
          </span>
          {user.is_admin ? (
            <span className={`${CHIP} border-sky-300 text-sky-800 dark:border-sky-800 dark:text-sky-200`}>
              Admin
            </span>
          ) : null}
          <span className={`${CHIP} ${accessChip}`}>{access.label}</span>
        </div>
      </div>

      {/* ⭐ The REASON, not just the label. "Enrolment pending" without an explanation is a status an
          admin has to go and ask somebody about. */}
      {access.kind !== 'ok' ? (
        <p className="mt-2 text-xs text-[color:var(--color-ink-muted)]">{access.detail}</p>
      ) : null}

      <p className="mt-2 text-xs text-[color:var(--color-ink-subtle)]">
        {user.active_sessions} active session{user.active_sessions === '1' ? '' : 's'}
        {user.last_login_at ? ` · last seen ${user.last_login_at.slice(0, 10)}` : ' · never signed in'}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          className={ACTION}
          disabled={busy || !actions.canChangeActive}
          title={actions.isSelf ? 'You cannot deactivate your own account' : undefined}
          onClick={() => onSetActive(user, !user.is_active)}
        >
          {user.is_active ? 'Deactivate' : 'Reactivate'}
        </button>

        <button
          className={ACTION}
          disabled={busy || !actions.canChangeAdmin}
          title={actions.isSelf ? 'You cannot change your own admin flag' : undefined}
          onClick={() => onSetAdmin(user, !user.is_admin)}
        >
          {user.is_admin ? 'Revoke admin' : 'Grant admin'}
        </button>

        {actions.canForceReenrol ? (
          <button className={ACTION} disabled={busy} onClick={() => onForceReenrol(user)}>
            Force 2FA re-enrolment
          </button>
        ) : null}

        {actions.canResetPassword ? (
          <button className={ACTION} disabled={busy} onClick={() => onIssueReset(user)}>
            Issue password reset
          </button>
        ) : null}

        {actions.canRevokeSessions ? (
          <button className={ACTION} disabled={busy} onClick={() => onRevokeSessions(user)}>
            Revoke sessions
          </button>
        ) : null}
      </div>

      {/* ⛔ BELOW the account actions, not among them. Membership is not an action on the account —
          it is a fact about where the person can work, and it needs its own reading space. */}
      {renderProjects ? renderProjects(user) : null}
    </li>
  );
}
